import React, {useState} from "react"
import {OTPFormData, FormErrors} from "../../types/auth"
import {validateOTPCode} from "../../utils/validations"

interface OTPFormProps {
	email: string
	onSubmit: (email: string, code: string) => Promise<void>
	onResend: (email: string) => Promise<void>
	onBack: () => void
	isLoading: boolean
}

export const OTPForm: React.FC<OTPFormProps> = ({email, onSubmit, onResend, onBack, isLoading}) => {
	const [formData, setFormData] = useState<OTPFormData>({code: ""})
	const [errors, setErrors] = useState<FormErrors>({})
	const [isResending, setIsResending] = useState(false)
	const [isVerifying, setIsVerifying] = useState(false)

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		const codeError = validateOTPCode(formData.code)
		if (codeError) {
			setErrors({code: codeError})
			return
		}

		setErrors({})
		setIsVerifying(true)

		try {
			await onSubmit(email, formData.code)
		} catch (error) {
			setErrors({general: error instanceof Error ? error.message : "An error occurred"})
		} finally {
			setIsVerifying(false)
		}
	}

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/\D/g, "").slice(0, 6)
		setFormData({code: value})

		if (errors.code && value) {
			const codeError = validateOTPCode(value)
			if (!codeError) {
				setErrors((prev) => ({...prev, code: undefined}))
			}
		}
	}

	const handleResend = async () => {
		setIsResending(true)
		setErrors({})

		try {
			await onResend(email)
		} catch (error) {
			setErrors({general: error instanceof Error ? error.message : "Failed to resend code"})
		} finally {
			setIsResending(false)
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="text-center">
				<h2 className="text-lg font-medium text-gray-900 mb-2">Enter Verification Code</h2>
				<p className="text-sm text-gray-600">
					We've sent a 6-digit code to <span className="font-medium">{email}</span>
				</p>
			</div>

			<div>
				<label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
					Verification Code
				</label>
				<input
					id="code"
					type="text"
					inputMode="numeric"
					maxLength={6}
					value={formData.code}
					onChange={handleCodeChange}
					className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg tracking-widest bg-white ${
						errors.code ? "border-red-500" : "border-gray-300"
					}`}
					placeholder="000000"
					autoFocus
				/>
				{errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
			</div>

			{errors.general && (
				<div className="p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-600">{errors.general}</p>
				</div>
			)}

			<div className="space-y-3">
				<button
					type="submit"
					disabled={isVerifying || formData.code.length !== 6}
					className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
					{isVerifying ? "Verifying..." : "Verify Code"}
				</button>

				<div className="flex justify-between items-center">
					<button
						type="button"
						onClick={onBack}
						disabled={isVerifying}
						className="text-sm text-gray-600 hover:text-gray-800 underline disabled:opacity-50">
						Back to email
					</button>

					<button
						type="button"
						onClick={handleResend}
						disabled={isResending || isVerifying}
						className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50">
						{isResending ? "Sending..." : "Resend code"}
					</button>
				</div>
			</div>
		</form>
	)
}
